import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import IdleWatcher from "./IdleWatcher";
import TopBar, { type Crumb } from "./TopBar";
import { Spinner } from "./ui";
import { TooltipProvider } from "./ui/tooltip";
import { getToken } from "@/lib/auth";
import { useLanguage } from "@/i18n";

/**
 * AppShell — authenticated page wrapper.
 *
 * Wraps the entire authenticated app in Radix's TooltipProvider so that any
 * Tooltip component anywhere in the tree can open without needing its own
 * Provider.  One Provider at the root is the recommended Radix pattern.
 */
export default function AppShell({
  crumbs,
  actions,
  children,
}: {
  crumbs?: Crumb[];
  /** Page buttons shown in the top bar, so a page needs no header row of its own. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (!getToken()) {
      // replace, not push -- an unauthenticated visit shouldn't leave a history
      // entry that the back button returns to.
      navigate("/login", { replace: true });
    } else {
      setAuthChecked(true);
    }
  }, [navigate]);

  if (!authChecked) {
    return (
      <div className="flex h-full w-full items-center justify-center gap-2 bg-canvas text-sm text-ink-muted">
        <Spinner />
        {t.appShell.checkingSession}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="sheet-bg flex h-full w-full overflow-hidden">
        <Sidebar />
        <IdleWatcher />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <TopBar crumbs={crumbs ?? [{ label: t.sidebar.projects, href: "/" }]} actions={actions} />
          <main className="thin-scroll min-h-0 flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}

